import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { useStartRemediationMutation } from "~/graphql/supergraphHooks";

export interface StartRemediationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  successMessage?: string;
}

export interface StartRemediationVariables {
  positionId: number;
  arenaId: number;
  emailAddresses: string[];
  message?: string;
  entityId: number;
  programName: string;
}

/**
 * Shared hook for starting remediation across different components.
 * Provides consistent error handling, success messaging, and cache invalidation.
 */
export const useStartRemediation = (options: StartRemediationOptions = {}) => {
  const { onSuccess, onError, successMessage = "Remediation started successfully" } = options;

  const [startRemediationMutation, mutationResult] = useStartRemediationMutation({
    onCompleted: () => {
      toast.success(successMessage);
      void client.refetchQueries({ include: "active" });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message);
      onError?.(error);
    },
  });

  const startRemediation = useCallback(
    (variables: StartRemediationVariables) => {
      return startRemediationMutation({ variables });
    },
    [startRemediationMutation],
  );

  return {
    startRemediation,
    loading: mutationResult.loading,
    error: mutationResult.error,
    data: mutationResult.data,
  };
};
